import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Language } from '../contexts/LanguageContext';
import App from '../App';

// Helper function to detect preferred language
const getPreferredLanguage = (): string => {
  // Check browser language
  const browserLang = navigator.language || (navigator as any).userLanguage;
  
  // If browser language is Estonian, use Estonian
  if (browserLang.startsWith('et')) {
    return 'et';
  }
  
  // Default to English for all other languages (including English)
  return 'en';
};

const LanguageWrapper: React.FC = () => {
  const { language } = useParams<{ language: Language }>();
  const { i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    if (language && ['en', 'et'].includes(language)) {
      if (i18n.language !== language) {
        i18n.changeLanguage(language);
      }
    }
  }, [language, i18n]);

  // If language is invalid, redirect to preferred language
  if (!language || !['en', 'et'].includes(language)) {
    const pathWithoutLang = location.pathname.replace(/^\/(en|et)/, '') || '/';
    const preferredLang = getPreferredLanguage();
    return <Navigate to={`/${preferredLang}${pathWithoutLang === '/' ? '' : pathWithoutLang}`} replace />;
  }

  return <App />;
};

const AppRouter: React.FC = () => {
  return (
    <Routes>
      {/* Language-specific routes */}
      <Route path="/:language/*" element={<LanguageWrapper />} />
      
      {/* Default route - redirect to preferred language */}
      <Route path="/*" element={<Navigate to={`/${getPreferredLanguage()}`} replace />} />
    </Routes>
  );
};

export default AppRouter;
